import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Pagination,
  styled,
  Stack,
} from "@mui/material";
import useFreeBookList from "../../hooks/api-request/useFreeBookList";
import BookListSkeleton from "../../components/skeletons/BookListSkeleton";
import EmptyState from "../../components/no-data/NoData";
import FreeBookCard from "../../components/cards/FreeBookCard";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const ResultCount = styled(Typography)(({ theme }) => ({
  fontSize: "1.125rem",
  fontWeight: 500,
  marginBottom: theme.spacing(3),
}));

// Custom styled pagination for better RTL support
const StyledPagination = styled(Pagination, {
  shouldForwardProp: (prop) => prop !== "isRtl"
})(({ theme, isRtl }) => ({
  "& .MuiPaginationItem-root": {
    margin: isRtl ? "0 0 0 4px" : "0 4px 0 0",
  },
  "& .MuiPaginationItem-icon": {
    transform: isRtl ? "rotate(180deg)" : "rotate(0deg)",
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiPaginationItem-root": {
      minWidth: 30,
      height: 30,
      fontSize: "0.8rem",
    }
  }
}));

const FreeBooks = () => {
  const [page, setPage] = useState(1);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  // Check if the language is RTL (Pashto or Dari)
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  
  // Get search query from URL
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get("search") || "";

  const { data, isLoading } = useFreeBookList(page, searchQuery);
  const books = data?.results || [];
  const totalPages = data?.total_pages || 1;

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Show active search query if exists */}
            {searchQuery && (
              <Box sx={{ mb: 3, textAlign: isRtl ? 'right' : 'left' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('freeBooks.searchResults', { query: searchQuery })}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12}>
            {/* Results Header */}
            <ResultCount sx={{ textAlign: isRtl ? 'right' : 'left' }}>
              {t('freeBooks.availableBooks', { count: data?.count || 0 })}
            </ResultCount>

            {/* Books Grid */}
            {isLoading ? (
              <BookListSkeleton />
            ) : books.length > 0 ? (
              <>
                <Grid container spacing={2} dir={isRtl ? 'rtl' : 'ltr'}>
                  {books.map((book) => (
                    <Grid item xs={6} sm={6} md={4} lg={3} key={book.id}>
                      <FreeBookCard book={book} />
                    </Grid>
                  ))}
                </Grid>

                <Stack
                  direction="row"
                  justifyContent="center" 
                  sx={{ mt: 4, width: "100%", overflow: "hidden" }}
                >
                  <StyledPagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                    isRtl={isRtl}
                    boundaryCount={1}
                    siblingCount={1}
                    dir={isRtl ? "rtl" : "ltr"}
                    shape="rounded"
                  />
                </Stack>
              </>
            ) : (
              <EmptyState message={t('freeBooks.noBooks')} />
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default FreeBooks;
